import { useCallback, useEffect, useMemo } from 'react'

import { useMutation, useQuery } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import type {
  PeopleApiGetPeopleRequest,
  Person,
} from '@b-stock/account-api-client'

import { useManagedAccessToken } from '@/app/AuthProvider'
import api from '@api/person'
import useUserInfo from '@helpers/useUserInfo'
import { checkAccessToken } from '@store/auth/login/actions'

import { useStatefulMutation } from '../mutation'
import { queryClient } from '../queryClient'

const baseKey = 'person'

export const usePerson = (personId?: string) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const params = location.search ? location.search : ''
  const currentUserPersonId = useUserInfo().personId
  const isLoggedIn = !!currentUserPersonId
  const id = personId || currentUserPersonId
  const queryKey = useMemo(() => [baseKey, id], [id])
  const queryListKey = useMemo(() => [...queryKey, 'list'], [queryKey])
  const updateKey = useMemo(() => [...queryKey, 'update'], [queryKey])
  const verifyEmailKey = useMemo(() => [...queryKey, 'verifyEmail'], [queryKey])

  const query = useQuery({
    queryKey: queryKey,
    queryFn: () => api.fetchPerson(id ?? 'UNAUTHENTICATED'),
    enabled: !!id,
  })

  const managedAccessToken = useManagedAccessToken()
  const refreshAccessToken = useCallback(() => {
    if (!managedAccessToken) {
      throw new Error(
        'person invalidateQuery called when a managed access token was unavailable - this is unexpected'
      )
    }
    dispatch(
      checkAccessToken(
        {
          tokenOnly: isLoggedIn,
          redirectToLogin: true,
          redirectAfterLogin: `${location.pathname}${params}`,
        },
        history,
        managedAccessToken
      )
    )
  }, [location, history, isLoggedIn, dispatch, params, managedAccessToken])

  const invalidateQuery = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey })
    refreshAccessToken()
  }, [queryKey, refreshAccessToken])

  // updatePerson's PATCH response is already the authoritative, freshly-written
  // record, so seed the cache with it directly instead of invalidating + re-fetching:
  // a GET issued immediately after the write can still observe the pre-write value
  // (read-after-write lag) and leave the UI showing the stale value.
  const applyPersonUpdate = useCallback(
    (data: Person) => {
      queryClient.setQueryData(queryKey, data)
      refreshAccessToken()
    },
    [queryKey, refreshAccessToken]
  )

  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey })
  }, [queryKey, currentUserPersonId])

  const updateMutation = useStatefulMutation<
    Person,
    AxiosError<{ errorMessage?: string }>,
    PersonPayload,
    any
  >(
    (payload: PersonPayload) => {
      if (!id) {
        throw new Error('updatePerson called without a person id')
      }
      return api.updatePerson(preparePersonToSave(id, payload))
    },
    {
      mutationKey: updateKey,
      onSuccess: applyPersonUpdate,
    }
  )

  // Fetching persons based on options
  const usersList = useStatefulMutation<
    any,
    AxiosError<{ errorMessage?: string }>,
    PeopleApiGetPeopleRequest,
    any
  >((payload: PeopleApiGetPeopleRequest) => api.fetchPersons(payload), {
    mutationKey: queryListKey,
    onSuccess: invalidateQuery,
  })

  const resendValidationEmail = useCallback(() => {
    if (!id) {
      throw new Error('resendValidationEmail called without a person id')
    }
    return api.resendValidationEmail({ id })
  }, [id])

  const verifyEmailMutation = useMutation({
    mutationFn: (payload: VerifyEmailPayload) => {
      if (!id) {
        throw new Error('verifyEmail called without person id')
      }
      return api.verifyEmail({ id, verificationCode: payload.verificationCode })
    },
    onSuccess: invalidateQuery,
    onError: invalidateQuery,
    mutationKey: verifyEmailKey,
  })

  return {
    ...query,
    usersList: usersList.mutateAsync,
    updatePerson: updateMutation.mutateAsync,
    updatePersonState: updateMutation.state,
    resendValidationEmail,
    verifyEmail: verifyEmailMutation,
  }
}

export type UsePerson = ReturnType<typeof usePerson>

type VerifyEmailPayload = {
  verificationCode: string
}

export type PersonPayload = {
  firstName?: string
  lastName?: string
  phone?: string
  email?: string
  pendingEmail?: string | null
  isEmailVerified?: boolean
}

const preparePersonToSave = (
  id: string,
  { firstName, lastName, phone, email }: PersonPayload
) => ({
  id,
  data: {
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...(phone ? { phone } : {}),
    ...(email ? { email } : {}),
  },
})
